import type { TextListProps } from '@oneaudi/unified-web-components';
import { TextList, TextListItem } from '@oneaudi/unified-web-components';
import parse from 'html-react-parser';
import type { ReactNode } from 'react';
import React, { Children, cloneElement } from 'react';
import styled from 'styled-components';
import xss from 'xss';
import { renderTextWithFootnotesReferences } from '../feature-service-types';

const StyledList = styled(TextList)`
  &.list li {
    margin-block-end: var(--spacing-relative-sm) !important;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    margin-inline-start: 10px;
  }
`;

const StyledListItem = styled(TextListItem)`
  margin-block-start: 0px;
  margin-block-end: var(--spacing-relative-sm);
`;
const transform = (node: ReactNode) => {
  if (typeof node === 'string') {
    return renderTextWithFootnotesReferences(node);
  }
  return node;
};

export function renderReactNodeWithFootnotes(nodes: ReactNode[]) {
  return Children.map(nodes, transform);
}

export const sanitizeText = (textString: string) => {
  if (textString) {
    return textString
      .replace(/&#8203;/gi, String.fromCharCode(8203)) // zero-width space
      .replace(/&zwnj;/gi, String.fromCharCode(8204)) // zero-width non-joiner
      .replace(/&nbsp;/gi, String.fromCharCode(160)) // non-breaking space
      .replace(/&#8209;/gi, String.fromCharCode(8209)) // non-breaking hyphen
      .replace(/&(?:amp;)?(?:shy|#0*173|#x0*0ad);/gi, String.fromCharCode(173)); // soft hyphen
  }
  return '';
};

const createElement = (
  type: string,
  props?: object,
  ...children: ReactNode[]
): React.JSX.Element => {
  const content = renderReactNodeWithFootnotes(children);

  switch (type) {
    case 'br':
      return React.createElement(type, props);

    case 'ul':
    case 'ol': {
      const variant = type === 'ul' ? 'bullet' : 'decimal';
      const enhancedProps: TextListProps = {
        ...props,
        size: 'md',
        variant,
        className: 'list',
      };
      return React.createElement(StyledList, enhancedProps, content);
    }

    case 'li':
      return React.createElement(StyledListItem, props, content);

    default:
      return React.createElement(type, props, content);
  }
};

export const sanitizeRichtext = (htmlString: string) =>
  xss(htmlString, {
    whiteList: {
      b: [],
      i: [],
      u: [],
      ul: [],
      ol: [],
      li: [],
      p: [],
      br: [],
      a: ['href', 'target'],
      span: [],
      strong: [],
    },
    stripIgnoreTagBody: ['script'],
    onIgnoreTag: (tag) => {
      if (tag.length > 0) {
        return '';
      }
      return undefined;
    },
  });

export function convertRichText(htmlString: string) {
  const cleanedString = sanitizeText(htmlString);
  const safeHtml = sanitizeRichtext(cleanedString);

  return parse(safeHtml, {
    library: {
      cloneElement,
      createElement,
      isValidElement: (element: unknown): boolean => {
        return Boolean(element);
      },
    },
  });
}
